"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FEDERAL_UNDETERMINED_REASON = void 0;
exports.situationFromRuleContext = situationFromRuleContext;
exports.facilityFacts = facilityFacts;
exports.isTimeLimit = isTimeLimit;
exports.isEpisodicWindow = isEpisodicWindow;
exports.isMoveWindow = isMoveWindow;
exports.timeBasisLabel = timeBasisLabel;
exports.timeLimitOf = timeLimitOf;
exports.rulesetFromThresholds = rulesetFromThresholds;
exports.toResolvedRuleset = toResolvedRuleset;
exports.toAccumulationThresholdJudgment = toAccumulationThresholdJudgment;
exports.thresholdRulesFor = thresholdRulesFor;
exports.resolveAccumulation = resolveAccumulation;
exports.resolveAccumulationThresholds = resolveAccumulationThresholds;
const states_1 = require("encamp-shared/dist/constants/states");
const facilityState_1 = require("../facilityState");
const jurisdictions_1 = require("../rules/jurisdictions");
const _FEDERAL_1 = require("../rules/jurisdictions/_FEDERAL");
const onSiteAccumulationThresholds_1 = require("../rules/onSiteAccumulationThresholds");
const stricterOfReason_1 = require("../rules/stricterOfReason");
exports.FEDERAL_UNDETERMINED_REASON = "Storage deadlines need this facility's generator status, which isn't known yet. " +
    "Generator status comes from the facility's EPA registration or its shipment history.";
/**
 * The situation a rule query describes. A query carries no container
 * history, so the container is an ordinary one with no event attached, and
 * its classification is left out: nothing in a query depends on it.
 */
function situationFromRuleContext(ctx) {
    return {
        facility: facilityFacts(ctx),
        area: { type: ctx.areaType },
        container: {
            wasteKind: ctx.wasteKind,
            operatesUnderSubpartP: ctx.operatesUnderSubpartP,
            mustTransport200Miles: ctx.mustTransport200Miles,
            attachedToActiveEpisodicEvent: false,
        },
    };
}
function facilityFacts(ctx) {
    return {
        ...(ctx.generatorStatus === null ? {} : { federalGeneratorStatus: ctx.generatorStatus }),
        ...(ctx.stateGeneratorStatus === null
            ? {}
            : { stateGeneratorStatus: ctx.stateGeneratorStatus }),
    };
}
/** The standing clock: CONTAINER-scoped, counted from the container's own history. */
function isTimeLimit(threshold) {
    if (threshold.scope !== "CONTAINER" || threshold.kind !== "DAYS_SINCE") {
        return false;
    }
    const from = threshold.from;
    return from === undefined || (typeof from === "string" && from !== "EPISODIC_START");
}
function isEpisodicWindow(threshold) {
    return threshold.kind === "DAYS_SINCE" && threshold.from === "EPISODIC_START";
}
/** A window a cap's crossing starts: the only thresholds anchored on another's crossing. */
function isMoveWindow(threshold) {
    return threshold.kind === "DAYS_SINCE" && typeof threshold.from === "object";
}
function isAmount(threshold, scope) {
    return threshold.scope === scope && threshold.kind === "WASTE_AMOUNT";
}
/** Whether an amount threshold sums exactly the given categories. */
function sumsExactly(threshold, categories) {
    const included = threshold.kind === "WASTE_AMOUNT" ? (threshold.includedCategories ?? []) : [];
    return (included.length === categories.length &&
        categories.every((category) => included.some((c) => c === category)));
}
function single(thresholds, what) {
    if (thresholds.length !== 1) {
        throw new Error(`Expected exactly one ${what}, got ${thresholds.length}.`);
    }
    return thresholds[0];
}
function noLimit(threshold) {
    return {
        kind: "NO_LIMIT",
        citation: threshold.source.citation,
        jurisdiction: threshold.source.jurisdiction,
    };
}
const AXIS_BY_FACT = {
    "facility.federalGeneratorStatus": "generatorStatus",
    "facility.stateGeneratorStatus": "stateGeneratorStatus",
};
function undetermined(missing, rules) {
    const missingAxes = missing.flatMap((fact) => {
        const axis = AXIS_BY_FACT[fact];
        return axis === undefined ? [] : [axis];
    });
    const reason = rules.stateStatusDomain.length > 0 && rules.name !== null
        ? (0, stricterOfReason_1.stricterOfReason)({
            federal: missingAxes.includes("generatorStatus"),
            state: missingAxes.includes("stateGeneratorStatus"),
        }, rules.name)
        : exports.FEDERAL_UNDETERMINED_REASON;
    return { kind: "UNDETERMINED", missingAxes, reason };
}
/** The subclassification program whose definition set a threshold, if any. */
function subclassificationOf(threshold) {
    const program = threshold.basis.container?.wasteKind;
    return program !== undefined && _FEDERAL_1.Federal.isSubclassification(program) ? program : undefined;
}
function timeLimitClock(threshold, rules) {
    if (threshold.kind !== "DAYS_SINCE") {
        throw new Error(`${threshold.id} is not a time limit.`);
    }
    switch (threshold.determination) {
        case "EXEMPT":
            return noLimit(threshold);
        case "UNDETERMINED":
            return undetermined(threshold.missing, rules);
        case "KNOWN":
            return {
                kind: "TIME",
                days: threshold.value,
                citation: threshold.source.citation,
                jurisdiction: threshold.source.jurisdiction,
            };
    }
}
function amountValue(threshold) {
    if (threshold.kind === "DAYS_SINCE" || threshold.determination !== "KNOWN") {
        throw new Error(`${threshold.id} has no known amount.`);
    }
    return threshold.value;
}
function satelliteCaps(areaThresholds, containerThresholds) {
    // The caps never sum waste a subclassification program governs, and that
    // program's clock cites why part 262 doesn't reach it.
    const timeLimit = timeLimitOf(containerThresholds);
    if (timeLimit !== null && subclassificationOf(timeLimit) !== undefined) {
        return noLimit(timeLimit);
    }
    const caps = areaThresholds.filter((threshold) => isAmount(threshold, "AREA"));
    const exempt = caps.find((cap) => cap.determination === "EXEMPT");
    if (exempt !== undefined) {
        return noLimit(exempt);
    }
    const windows = containerThresholds.filter(isMoveWindow);
    if (windows.length === 0) {
        // Only an active episodic event's waste counts toward none of the caps.
        return noLimit(single(containerThresholds.filter(isEpisodicWindow), "episodic window"));
    }
    const slot = (category, unit) => single(caps.filter((cap) => cap.kind === "WASTE_AMOUNT" && sumsExactly(cap, [category]) && cap.unit === unit), `satellite cap for ${category} ${unit}`);
    const windowDays = new Set(windows.flatMap((window) => window.kind === "DAYS_SINCE" && window.value !== undefined ? [window.value] : []));
    if (windowDays.size !== 1) {
        throw new Error(`Expected one move window length, got ${[...windowDays].join(", ")}.`);
    }
    const nonAcute = slot("NON_ACUTE", "GALLONS");
    return {
        kind: "VOLUME",
        nonAcuteGal: amountValue(nonAcute),
        acuteGal: amountValue(slot("ACUTE", "GALLONS")),
        acuteKg: amountValue(slot("ACUTE", "KILOGRAMS")),
        moveWindowDays: [...windowDays][0],
        citation: nonAcute.source.citation,
        jurisdiction: nonAcute.source.jurisdiction,
    };
}
const SUBCLASSIFICATION_BASIS_LABELS = {
    UNIVERSAL_WASTE: "universal waste",
    USED_OIL: "used oil",
    PHARMACEUTICAL: "pharmaceutical waste",
};
// Matches the retired rule records' basis strings: federal central rules were
// written per generator tier; state rules decide the tier internally, which
// the records rendered as "any generator". A clock a subclassification
// program set runs off that program rather than a generator tier.
function timeBasisLabel(timeLimit, query) {
    const subclassification = subclassificationOf(timeLimit);
    if (subclassification !== undefined) {
        return SUBCLASSIFICATION_BASIS_LABELS[subclassification];
    }
    if (timeLimit.source.jurisdiction === "FEDERAL" && query.generatorStatus !== null) {
        return query.generatorStatus;
    }
    return "any generator";
}
/** The standing clock among a container's thresholds; null where none applies. */
function timeLimitOf(containerThresholds) {
    const timeLimits = containerThresholds.filter(isTimeLimit);
    return timeLimits.length === 0 ? null : single(timeLimits, "time limit");
}
/**
 * Today's ruleset shape from a container's thresholds and its area's. Only
 * the area type is read off the situation.
 */
function rulesetFromThresholds(rules, areaType, containerThresholds, areaThresholds) {
    const timeLimit = timeLimitOf(containerThresholds);
    const clock = timeLimit === null ? null : timeLimitClock(timeLimit, rules);
    if (areaType === "CENTRAL") {
        if (clock === null) {
            throw new Error("A central container has no time limit.");
        }
        return { areaType: "CENTRAL", clock };
    }
    if (clock?.kind === "UNDETERMINED") {
        throw new Error(`${timeLimit?.id} is undetermined in a satellite area.`);
    }
    return {
        areaType: "SATELLITE",
        caps: satelliteCaps(areaThresholds, containerThresholds),
        clock,
    };
}
/** Today's ruleset shape for a rule query, rebuilt from the jurisdiction's thresholds. */
function toResolvedRuleset(rules, ctx) {
    const situation = situationFromRuleContext(ctx);
    return rulesetFromThresholds(rules, ctx.areaType, rules.thresholdsFor(situation), rules.thresholdsFor({ facility: situation.facility, area: situation.area }));
}
/** Today's on-site judgment shape, rebuilt from the jurisdiction's facility thresholds. */
function toAccumulationThresholdJudgment(rules, ctx) {
    const facility = facilityFacts(ctx);
    const thresholds = rules.thresholdsFor({ facility });
    if (thresholds.some((threshold) => threshold.determination === "UNDETERMINED")) {
        return { thresholds: onSiteAccumulationThresholds_1.NO_ACCUMULATION_THRESHOLDS, judgedAgainst: null };
    }
    const slots = { ...onSiteAccumulationThresholds_1.NO_ACCUMULATION_THRESHOLDS };
    for (const threshold of thresholds) {
        if (threshold.kind !== "WASTE_AMOUNT" || threshold.determination !== "KNOWN") {
            continue;
        }
        if (sumsExactly(threshold, ["ACUTE", "NON_ACUTE"])) {
            slots.totalKg = threshold.value;
        }
        else if (sumsExactly(threshold, ["NON_ACUTE"])) {
            slots.nonAcuteKg = threshold.value;
        }
        else if (sumsExactly(threshold, ["ACUTE"])) {
            slots.acuteKg = threshold.value;
        }
    }
    return { thresholds: slots, judgedAgainst: rules.describeGeneratorStatus(facility) };
}
/** The jurisdiction answering for a facility's state, as the threshold adapter reads it. */
function thresholdRulesFor(state) {
    const rules = (0, jurisdictions_1.jurisdictionRulesFor)(state);
    const parsed = (0, facilityState_1.parseFacilityState)(state);
    return {
        thresholdsFor: (situation) => rules.thresholdsFor(situation),
        describeGeneratorStatus: (facility) => rules.describeGeneratorStatus(facility),
        stateStatusDomain: rules.stateStatusDomain,
        name: parsed === null ? null : (states_1.stateNamesByAbbreviation.get(parsed) ?? parsed),
    };
}
function resolveAccumulation(jurisdiction, ctx) {
    return toResolvedRuleset(thresholdRulesFor(jurisdiction), ctx);
}
function resolveAccumulationThresholds(jurisdiction, ctx) {
    return toAccumulationThresholdJudgment(thresholdRulesFor(jurisdiction), ctx);
}
//# sourceMappingURL=adapter.js.map